import { useTrainer } from "api/hooks";
import { PaziaBadge, PaziaBox, PaziaBreadcrumbs } from "components/atoms";
import { DeleteButton } from "components/atoms/Buttons";
import { AnchorIcon, LocationIcon } from "components/atoms/Icons";
import { FaBoxOpen, FaBullhorn, FaCalendarDay, FaEnvelope, FaPhone, FaRegClock } from "react-icons/fa";

import { AdminResourceDeleteModal, PageMain, UserAvatar } from "components/molecules";
import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { htmlParser } from "utils/htmlParser";
import { toMoneyFormat } from "utils/moneyUtils";
import { useToaster } from "utils";
import "@szhsin/react-menu/dist/index.css";
import { TrainerCalendar } from "./TrainerCalendar";
import { TrainerWorkHistory } from "./TrainerWorkHistory";
import { TrainerAvailabilityAdminPanel } from "./TrainerAvailabilityAdminPanel";
import { TrainerFilesPanel } from "./TrainerFilesPanel";
import { TrainerAccountPanel } from "./TrainerAccountPanel";
import { TrainerLectureConfirmationsTable } from "pages/TrainerLectureConfirmations/TrainerLectureConfirmationsTable";
import { deleteResourceActions, DeleteResourceContext } from "utils/store";
import { trainersApi } from "api";
import { routes, PAYMENT_TYPE, PAYMENT_TYPE_OPTIONS } from "consts";
import { TrainerApi } from "api/types";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { useQuery } from "@tanstack/react-query";
import { TrainerTopicApi, trainerTopicsApi } from "api/trainerTopicsApi";
import { StudentIconClipboardText } from "pages/Students/Profile/StudentPage";

const tabs = [
  { name: "Ogólne" },
  { name: "Tematy" },
  { name: "Dostępność" },
  { name: "Faktury i pliki" },
  { name: "Potwierdzenia obecności" },
  { name: "Konto" },
  //  { name: "Kalendarz" },
];

export const TrainerPage = () => {
  const { dispatch } = useContext(DeleteResourceContext);
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const { data, loading, refetch } = useTrainer(trainerId);
  const [currentTab, setCurrentTab] = useState(0);

  if (!data || loading) return <PaziaLoader position="center" />;

  const onDelete = () => {
    deleteResourceActions.setDeleteResource(dispatch, data.id);
  };

  const { firstName, surname, title, alternativeEmail, inpost } = data;

  return (
    <PageMain>
      <PaziaBreadcrumbs
        segments={[{ title: "Trenerzy", path: routes.TRAINERS }, { title: firstName + " " + surname }]}
      />
      <PaziaBox className="mt-4 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
          <div className="flex min-w-0">
            <div className="shrink-0">
              <UserAvatar avatar={data.avatar} size="smallMedium" firstName={firstName} surname={surname} />
            </div>
            <div className="min-w-0 px-4">
              <div className="pb-2 text-2xl font-bold text-slate-800 [overflow-wrap:anywhere] dark:text-slate-100">
                {title ? `${title} ` : ""}
                {firstName} {surname}
              </div>
              <StudentIconClipboardText text={data.email} icon={<AnchorIcon />} />
              {alternativeEmail && (
                <StudentIconClipboardText
                  text={alternativeEmail}
                  icon={<FaEnvelope />}
                  className="text-slate-400 dark:text-slate-500"
                />
              )}
              <StudentIconClipboardText
                text={data.phone}
                icon={<FaPhone />}
                className="text-slate-400 dark:text-slate-500"
              />
              <StudentIconClipboardText
                text={data.address ? `${data.address}, ${data.postCode} ${data.city}` : ""}
                icon={<LocationIcon />}
              />
              {inpost && (
                <StudentIconClipboardText
                  text={inpost}
                  displayText={`Paczkomat: ${inpost}`}
                  icon={<FaBoxOpen />}
                  className="text-slate-400 dark:text-slate-500"
                />
              )}

              {data.website && (
                <div className="flex items-center">
                  <AnchorIcon />
                  <a
                    className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 ml-2 min-w-0 text-sm font-medium [overflow-wrap:anywhere]"
                    href={data.website}
                    target="_blank"
                  >
                    {data.website}
                  </a>
                </div>
              )}
            </div>
          </div>
          <div className="self-end sm:self-start">
            <div className="flex gap-2">
              <PaziaButton title="Edytuj" type="tertiary" onClick={() => navigate(routes.TRAINERS_EDIT(trainerId))} />
              <DeleteButton onClick={onDelete} />
            </div>
          </div>
        </div>
        <div className="my-3 flex flex-wrap gap-2">
          {data.groups?.map((gr) => (
            <PaziaBadge color="slate" size="xs" key={gr.name}>
              {gr.name}
            </PaziaBadge>
          ))}
        </div>
      </PaziaBox>
      <PaziaBox className="mt-4 p-4">
        <div className="relative pb-8">
          <div className="relative mb-6">
            <div className="absolute bottom-0 h-px w-full bg-slate-200 dark:bg-slate-700" aria-hidden="true"></div>
            <ul className="no-scrollbar relative flex flex-wrap gap-y-2 text-sm font-medium sm:-mx-6 sm:flex-nowrap sm:overflow-x-scroll lg:-mx-8">
              {tabs.map((tab, idx) => (
                <li
                  key={tab.name}
                  className="mr-6 cursor-pointer last:mr-0 sm:first:pl-6 sm:last:pr-6 lg:first:pl-8 lg:last:pr-8"
                  onClick={() => setCurrentTab(idx)}
                >
                  <a
                    className={
                      currentTab === idx
                        ? "text-primary-500 border-primary-500 block whitespace-nowrap border-b-2 pb-3"
                        : "block whitespace-nowrap pb-3 text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
                    }
                  >
                    {tab.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col">
            {currentTab === 0 && (
              <GeneralTrainer
                trainerId={Number(trainerId)}
                description={data.description}
                shortDescription={data.shortDescription}
                prices={data.prices}
              />
            )}
            {currentTab === 1 && <TrainerTopics trainerId={Number(trainerId)} />}
            {currentTab === 2 && <TrainerAvailabilityAdminPanel trainerId={Number(trainerId)} />}
            {currentTab === 3 && <TrainerFilesPanel trainerId={Number(trainerId)} />}
            {currentTab === 4 && <TrainerLectureConfirmationsTable trainerId={Number(trainerId)} />}
            {currentTab === 5 && (
              <TrainerAccountPanel
                trainerId={Number(trainerId)}
                email={data.email}
                userId={data.userId}
                userEmail={data.userEmail ?? null}
                onAccountGenerated={refetch}
              />
            )}
            {/* {currentTab ===2  && <TrainerCalendar />} */}
          </div>
        </div>
      </PaziaBox>
      <AdminResourceDeleteModal
        title={`Usuń trenera ${data.firstName} ${data.surname}`}
        description="Jesteś pewien że chcesz usunąć trenera?"
        onSuccessDelete={() => {
          navigate(routes.TRAINERS);
        }}
        deleteAction={trainersApi.delete}
      />
    </PageMain>
  );
};

interface GeneralTrainerProps {
  trainerId: number;
  description: string;
  shortDescription: string;
  prices: TrainerApi["prices"];
}
const GeneralTrainer = ({ trainerId, description, shortDescription, prices }: GeneralTrainerProps) => {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      <div className="col-span-12 space-y-5 md:col-span-8">
        {shortDescription && (
          <p className="border-primary-200 dark:border-primary-700 border-l-2 pl-3 text-sm italic text-slate-600 dark:text-slate-300">
            {shortDescription}
          </p>
        )}
        <div>
          <h2 className="mb-2 font-semibold text-slate-800 dark:text-slate-100">Opis</h2>
          <div className="space-y-2 text-sm">{htmlParser.toHTML(description ?? "")}</div>
        </div>

        <TrainerWorkHistory trainerId={trainerId} />
      </div>
      <div className="col-span-12 md:col-span-4">
        <TrainerFinancePanel prices={prices} />
      </div>
    </div>
  );
};

const PAYMENT_TYPE_ICON: Record<number, React.ReactNode> = {
  [PAYMENT_TYPE.PER_HOUR]: <FaRegClock size={13} />,
  [PAYMENT_TYPE.PER_DAY]: <FaCalendarDay size={13} />,
  [PAYMENT_TYPE.PER_OCCURANCE]: <FaBullhorn size={13} />,
};

interface TrainerFinancePanelProps {
  prices: TrainerApi["prices"];
}
const TrainerFinancePanel = ({ prices }: TrainerFinancePanelProps) => {
  const { showInfo } = useToaster();

  const onCopyAmount = (amount: number) => {
    navigator.clipboard.writeText(String(amount));
    showInfo("Skopiowano");
  };

  return (
    <div>
      <h2 className="mb-2 font-semibold text-slate-800 dark:text-slate-100">Finanse</h2>
      {!prices || prices.length < 1 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Brak zdefiniowanych stawek.</div>
      ) : (
        <div className="space-y-2">
          {prices.map((price, idx) => (
            <div
              key={idx}
              onClick={() => onCopyAmount(price.amount)}
              title="Kliknij, aby skopiować stawkę"
              className="hover:border-primary-300 dark:hover:border-primary-600 flex cursor-pointer items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white p-3 shadow-sm duration-150 ease-in-out dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400 dark:text-slate-500">{PAYMENT_TYPE_ICON[price.type]}</span>
                <div>
                  <div className="font-medium text-slate-800 dark:text-slate-100">{price.name}</div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {PAYMENT_TYPE_OPTIONS.find((x) => x.value === price.type)?.label}
                  </div>
                </div>
              </div>
              <div className="text-primary-600 dark:text-primary-400 whitespace-nowrap text-sm font-semibold">
                {toMoneyFormat(price.amount, true)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface TrainerTopicsProps {
  trainerId: number;
}

const TrainerTopics = ({ trainerId }: TrainerTopicsProps) => {
  const { isLoading, data } = useQuery({
    queryKey: ["fetchTrainerTopics", trainerId],
    queryFn: () => trainerTopicsApi.fetchByTrainerId(Number(trainerId)),
  });

  const columns = React.useMemo<TanTableColumn<TrainerTopicApi>[]>(() => cols, []);
  if (isLoading) return <PaziaLoader />;
  if (!data?.data) return null;

  return (
    <div>{data.data?.length > 0 ? <TanTableClient data={data.data} columns={columns} /> : <div>Brak tematów</div>}</div>
  );
};

const cols: TanTableColumn<TrainerTopicApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    id: "title",
    accessorKey: "title",
    header: "Tytuł",
    size: 500,
    enableColumnFilter: false,
    cell: (info) => (
      <a href={routes.TOPICS_EDIT(info.row.original.id.toString())} target="_blank" className="hover:underline">
        {info.getValue<number>()}
      </a>
    ),
  },
];
